import React, { useEffect, useMemo, useState } from "react";
import { Card, Checkbox, Col, Form, Row } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import isEmpty from "lodash/isEmpty";
import { Dayjs } from "dayjs";
import styles from "src/styles";

// Configs
import antDesignConfig from "src/configs/antd.config";

// Constants
import { CarLocationServices } from "src/constants";

// Services
import { getCarSearchServiceType } from "src/services/booking.service";

// Components
import SameLocationForm from "src/components/booking/SameLocationForm";
import DifferentLocationsForm from "src/components/booking/DifferentLocationsForm";
import DeliveryLocationForm from "src/components/booking/DeliveryLocationForm";

export type CarLocationServiceForm = {
    pickUpLocation?: number;
    returnLocation?: number;
    pickUpDate?: Dayjs;
    pickUpTime?: string;
    returnDate?: Dayjs;
    returnTime?: string;
};

export type CarSearchForm = {
    serviceType?: Record<
        CarLocationServices.DELIVERY | CarLocationServices.SAME_LOCATION,
        boolean
    >;
    locations?: {
        [CarLocationServices.SAME_LOCATION]?: CarLocationServiceForm;
        [CarLocationServices.DIFFERENT_LOCATIONS]?: CarLocationServiceForm;
        [CarLocationServices.DELIVERY]?: CarLocationServiceForm;
    };
};

type Props = {
    formName?: string;
    defaultDeliveryOptions?: {
        pickUp?: SelectOption[];
        return?: SelectOption[];
    };
    initialValues?: CarSearchForm;
    locationOptions?: SelectOption[];
    immediatelySubmit?: boolean;
    fetchAddressOptions: (value?: string) => Promise<SelectOption[]>;
    onSubmit?: (values: CarSearchForm) => void;
};

const CarSearchCard = ({
    formName,
    defaultDeliveryOptions = {},
    initialValues = {},
    locationOptions = [],
    immediatelySubmit = false,
    fetchAddressOptions,
    onSubmit = () => {},
}: Props) => {
    const intl = useIntl();
    const [form] = Form.useForm<CarSearchForm>();
    const [serviceType, setServiceType] = useState<CarSearchServiceType>(
        getCarSearchServiceType(initialValues?.serviceType)
    );

    useEffect(() => {
        if (immediatelySubmit && !isEmpty(initialValues)) {
            form.submit();
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleServiceTypeChange = (
        field: CarSearchServiceType,
        value: boolean
    ) => {
        const newValues = {
            [CarLocationServices.SAME_LOCATION]: false,
            [CarLocationServices.DELIVERY]: false,
            [field]: value,
        };
        form.setFieldsValue({ serviceType: newValues });
        setServiceType(getCarSearchServiceType(newValues));
    };

    const locationForm = useMemo(() => {
        switch (serviceType) {
            case CarLocationServices.DIFFERENT_LOCATIONS:
                return (
                    <DifferentLocationsForm locationOptions={locationOptions} />
                );
            case CarLocationServices.SAME_LOCATION:
                return <SameLocationForm locationOptions={locationOptions} />;
            case CarLocationServices.DELIVERY:
                return (
                    <DeliveryLocationForm
                        defaultPickUpOptions={defaultDeliveryOptions.pickUp}
                        defaultReturnOptions={defaultDeliveryOptions.return}
                        fetchAddressOptions={fetchAddressOptions}
                    />
                );
            default:
                return null;
        }
    }, [
        serviceType,
        locationOptions,
        defaultDeliveryOptions.pickUp,
        defaultDeliveryOptions.return,
        fetchAddressOptions,
    ]);

    return (
        <CardStyled
            className="car-search-card"
            title={intl.formatMessage({ id: "carSearchCard.title" })}
        >
            <Form
                name={formName}
                form={form}
                layout="vertical"
                initialValues={initialValues}
                requiredMark={false}
                onFinish={onSubmit}
                autoComplete="off"
                scrollToFirstError={antDesignConfig.scrollBehavior}
            >
                <Row>
                    <Col span={24} className="service-types">
                        <Form.Item
                            name={["serviceType", "sameLocation"]}
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox
                                onChange={(e) =>
                                    handleServiceTypeChange(
                                        CarLocationServices.SAME_LOCATION,
                                        e.target.checked
                                    )
                                }
                            >
                                <FormattedMessage id="carSearchCard.returnTheSameLocation" />
                            </Checkbox>
                        </Form.Item>
                        <Form.Item
                            name={["serviceType", "delivery"]}
                            valuePropName="checked"
                            noStyle
                        >
                            <Checkbox
                                onChange={(e) =>
                                    handleServiceTypeChange(
                                        CarLocationServices.DELIVERY,
                                        e.target.checked
                                    )
                                }
                            >
                                <FormattedMessage id="carSearchCard.wantPickUpAndDeliveryService" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24} className="location-form-section">
                        {locationForm}
                    </Col>
                </Row>
            </Form>
        </CardStyled>
    );
};

const CardStyled = styled(Card)`
    &.car-search-card {
        border-radius: 4px;

        .ant-card-head {
            border: none;
            padding: 24px 40px 12px 40px;

            @media screen and (max-width: 1199px) {
                padding: 24px 40px 12px 40px;
            }

            @media screen and (max-width: 767px) {
                padding: 10px 20px 10px 20px;
            }

            .ant-card-head-title {
                font-style: normal;
                font-weight: 500;
                font-size: 24px;
                line-height: 38px;
                color: ${styles.fontColorBlack};
                padding: 0;
                white-space: normal;

                @media screen and (max-width: 991px) {
                    font-size: 20px;
                }
            }
        }

        .ant-card-body {
            padding: 0 40px 20px 40px;

            @media screen and (max-width: 1199px) {
                padding: 0 40px 20px 40px;
            }

            @media screen and (max-width: 767px) {
                padding: 0 20px 20px 20px;
            }
        }

        .service-types {
            margin-bottom: 12px;

            @media screen and (max-width: 767px) {
                margin-bottom: 10px;
            }

            .ant-checkbox-wrapper {
                @media screen and (max-width: 487px) {
                    margin-left: 0;
                    margin-bottom: 0px;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }
        }
    }
`;

export default CarSearchCard;
